/**
 * Login return-path helper.
 *
 * When an authenticated-only page (today: the standalone meet routes,
 * `standaloneMeetRoutes.tsx`) bounces a logged-out visitor to `/login`, it
 * carries the ORIGINAL path in router state as `{ from: location.pathname }`.
 * After a successful login, `LoginForm` (and the `UnProtected` wrapper's
 * already-logged-in redirect, which can win the post-login race) send the user
 * back there instead of to `/`.
 *
 * Reading is deliberately strict: only same-app ABSOLUTE paths are honored.
 * Router state is attacker-influenceable (history.state survives reloads), so
 * protocol-relative (`//evil`) and absolute-URL shapes are rejected — never a
 * navigation target. Rejection falls back to `null` (callers default to `/`).
 */
export function readLoginReturnPath(state: unknown): string | null {
  if (typeof state !== "object" || state === null) {
    return null;
  }
  const from = (state as { from?: unknown }).from;
  if (typeof from !== "string") {
    return null;
  }
  // Internal path only: must start with exactly one "/" ("//host" is a
  // protocol-relative URL; "http://…" fails the startsWith check). Backslashes
  // are rejected wholesale: the URL spec treats "\\" as "/" in special schemes,
  // so "/\\evil.host" would resolve protocol-relative and make
  // history.replaceState throw a same-origin SecurityError (login crash).
  if (!from.startsWith("/") || from.startsWith("//") || from.includes("\\")) {
    return null;
  }
  return from;
}
